/**
 * Small, non-visual helpers for the HR staff order tab.
 */
import type { StaffOrderRoundStatus, StaffOrderRoundSummary } from '@/services/api/staffOrderService'

export const STATUS_BADGE: Record<StaffOrderRoundStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'border-transparent bg-slate-100 text-slate-700 hover:bg-slate-100' },
  open: { label: 'Open', className: 'border-transparent bg-emerald-100 text-emerald-700 hover:bg-emerald-100' },
  closed: { label: 'Closed', className: 'border-transparent bg-gray-200 text-gray-700 hover:bg-gray-200' },
}

/** Emails go out in the background at about this rate. */
export const EMAILS_PER_MINUTE = 25

/** About how many minutes a send to `count` people takes (at least 1). */
export function minutesToSend(count: number): number {
  return Math.max(1, Math.ceil(count / EMAILS_PER_MINUTE))
}

/** 1234 → "1,234" (the same grouping as the money on these pages). */
export function formatCount(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** "1 minute" / "12 minutes". */
export function minutesLabel(n: number): string {
  return `${n} ${n === 1 ? 'minute' : 'minutes'}`
}

/**
 * The round to show first: an open one, else a draft, else the newest.
 * Within each, the most recently created wins.
 */
export function pickDefaultRound(rounds: StaffOrderRoundSummary[]): string | null {
  if (rounds.length === 0) return null
  const newestFirst = [...rounds].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
  return (
    newestFirst.find((r) => r.status === 'open') ??
    newestFirst.find((r) => r.status === 'draft') ??
    newestFirst[0]
  ).id
}

/** Save a downloaded file through the browser. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
